import React, { useCallback, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import throttle from "lodash.throttle";
import { useDragLayer, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import SchedulingDropZoneItemRenderer from "./SchedulingDropZoneItemRenderer";
import TemplateDropZoneItemRenderer from "./TemplateDropZoneItemRenderer";
import {
  computeItemEndTimeLoopTimeline,
  computeItemStartTimeLoopTimeline,
  TimelineRenderTypes,
} from "./timeline";
import COLORS from "../../../constants/colors";
import { DRAG_TYPE } from "../../../constants/dragTypes";
import {
  DROPZONE_BORDER_SIZE,
  MAX_TIMELINE_Z_INDEX,
  OBJECT_TYPES_SCHEDULE_DROPZONE,
} from "../../../constants/fast";
import useFAST from "../../../contexts/FASTContext";
import {
  getBorderRadiusTimelineItem,
  getBorderWidthPosition,
  parseCssTextValue,
} from "../../../utils/fast";

import type { Truncated } from "okast-timeline";
import type { CSSProperties, DragEvent, JSX } from "react";
import type { DragLayerMonitor } from "react-dnd";

import type {
  SchedulingDropzoneRendererProps,
  TemplatingDropzoneRendererProps,
} from "./ItemRenderer";
import type { ItemDimensions } from "./timeline.types";
import type { MediaLibraryDragItem } from "../../../types";

const MAGNETIC_ZONE_SIZE = 10;

interface CollectedDragProps {
  isOver: boolean;
  canDrop: boolean;
}

type SchedulingDropZoneItemRendererProps = Pick<
  SchedulingDropzoneRendererProps,
  | "item"
  | "itemContext"
  | "getItemProps"
  | "dropzoneAcceptedFormats"
  | "isReplaceMode"
  | "timelineRef"
  | "previewMediaTooltipRef"
  | "onDropItem"
  | "renderType"
>;

type TemplatingDropZoneItemRendererProps = Pick<
  TemplatingDropzoneRendererProps,
  | "item"
  | "itemContext"
  | "getItemProps"
  | "dropzoneAcceptedFormats"
  | "isReplaceMode"
  | "timelineRef"
  | "onDropItem"
  | "renderType"
>;

type DropZoneItemRendererProps =
  | TemplatingDropZoneItemRendererProps
  | SchedulingDropZoneItemRendererProps;

type ConditionalDropZoneItemRendererProps<T> =
  T extends TimelineRenderTypes.TEMPLATING
    ? TemplatingDropZoneItemRendererProps
    : T extends TimelineRenderTypes.SCHEDULING
      ? SchedulingDropZoneItemRendererProps
      : never;

function DropZoneItemRenderer<
  T extends DropZoneItemRendererProps["renderType"],
>(props: ConditionalDropZoneItemRendererProps<T>): JSX.Element | null {
  const { t } = useTranslation("editor-fast-channel");

  const {
    getItemProps,
    itemContext,
    item,
    isReplaceMode,
    dropzoneAcceptedFormats,
    timelineRef,
    onDropItem,
    renderType,
  } = props;

  const [previewItemStartPosition, setPreviewItemStartPosition] = useState<
    number | null
  >(null); // top position for vertical timeline and left position for horizontal timeline
  const [previewItemStartTime, setPreviewItemStartTime] = useState<
    number | null
  >(null);
  const [truncatedPreviewItem, setTruncatedPreviewItem] =
    useState<Truncated>(null);

  const { state, dispatch } = useFAST();

  const { activeDropZoneId, timelineLoopPreviewMedia } = state;

  const isDropzonePreviewMedia =
    timelineLoopPreviewMedia?.dropzoneId === item.id;

  const isVerticalTimeline = Boolean(timelineRef?.current?.props.isVertical);
  const minTimeStartTimeline = timelineRef?.current?.props.minTimeStart;
  const maxTimeEndTimeline = timelineRef?.current?.props.maxTimeEnd;
  const isLoopTimeline =
    timelineRef?.current?.props.isLoop &&
    minTimeStartTimeline &&
    maxTimeEndTimeline;

  const itemsTimeline = timelineRef?.current?.state.items;

  const dropzoneItems = itemsTimeline?.filter(
    (itemTimeline) => itemTimeline.type === OBJECT_TYPES_SCHEDULE_DROPZONE,
  );

  const dropzoneOnStartMaxRange = dropzoneItems?.find(
    (dropzone) => dropzone.start_time.valueOf() === minTimeStartTimeline,
  );

  const dropzoneOnEndMaxRange = dropzoneItems?.find(
    (dropzone) => dropzone.end_time.valueOf() === maxTimeEndTimeline,
  );

  const isDropzoneOnStartMaxRange = dropzoneOnStartMaxRange?.id === item.id;

  const isDropzoneOnEndMaxRange = dropzoneOnEndMaxRange?.id === item.id;

  const hasDropzonesOnMaxRange =
    dropzoneOnStartMaxRange?.id && dropzoneOnEndMaxRange?.id;

  const dropzoneDuration = useMemo(() => {
    if (
      isLoopTimeline &&
      hasDropzonesOnMaxRange &&
      (isDropzoneOnEndMaxRange || isDropzoneOnStartMaxRange)
    ) {
      return dropzoneOnStartMaxRange.duration + dropzoneOnEndMaxRange.duration;
    }

    return item.duration;
  }, [
    dropzoneOnEndMaxRange,
    dropzoneOnStartMaxRange,
    hasDropzonesOnMaxRange,
    isDropzoneOnEndMaxRange,
    isDropzoneOnStartMaxRange,
    isLoopTimeline,
    item.duration,
  ]);

  const handleDropItem = (itemDrag: MediaLibraryDragItem): void => {
    if (isLoopTimeline) {
      dispatch({
        type: "DELETE_LOOP_TIMELINE_PREVIEW_MEDIA",
      });
    }

    if (previewItemStartTime && onDropItem) {
      const scheduleId = "scheduleId" in item ? item?.scheduleId : null;
      const maxEndTime = item.end_time.valueOf();

      onDropItem({
        item: itemDrag,
        startTime: previewItemStartTime,
        scheduleId,
        maxEndTime,
      });
    }
  };

  const { truncated: truncatedItemContext } = itemContext.dimensions;

  const truncatedDropzone = useMemo((): Truncated => {
    if (hasDropzonesOnMaxRange && isDropzoneOnStartMaxRange) {
      if (!truncatedItemContext) {
        return "start";
      }

      if (truncatedItemContext === "end") {
        return "start-end";
      }
    }

    if (hasDropzonesOnMaxRange && isDropzoneOnEndMaxRange) {
      if (!truncatedItemContext) {
        return "end";
      }

      if (truncatedItemContext === "start") {
        return "start-end";
      }
    }

    return truncatedItemContext;
  }, [
    hasDropzonesOnMaxRange,
    isDropzoneOnEndMaxRange,
    isDropzoneOnStartMaxRange,
    truncatedItemContext,
  ]);

  const { isDragging, itemDrag } = useDragLayer<{
    isDragging: boolean;
    itemDrag: MediaLibraryDragItem | null;
  }>((monitor: DragLayerMonitor<MediaLibraryDragItem | null>) => ({
    isDragging:
      monitor.isDragging() &&
      typeof monitor.getItemType() === "string" &&
      (monitor.getItemType() as string).includes(DRAG_TYPE.MEDIA_LIBRARY_ITEM),
    itemDrag: monitor.getItem(),
  }));

  const dropRef = useRef<HTMLDivElement>(null);

  const [{ canDrop, isOver }, dropConnector] = useDrop<
    MediaLibraryDragItem,
    unknown,
    CollectedDragProps
  >(
    () => ({
      accept: dropzoneAcceptedFormats,
      drop: handleDropItem,
      canDrop: (draggedItem: MediaLibraryDragItem): boolean =>
        !!draggedItem && dropzoneDuration >= draggedItem.duration,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [previewItemStartTime, previewItemStartPosition, item],
  );

  dropConnector(dropRef);

  const styleContainerItem: CSSProperties = {
    backgroundColor: "transparent",
    cursor: "default",
    border: "none",
  };

  const itemProps = getItemProps({ style: styleContainerItem });

  const {
    style,
    ref,
    onContextMenu,
    onDoubleClick,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    onTouchStart,
  } = itemProps;

  const dropZoneWidth =
    style.width && typeof style.width === "string"
      ? parseCssTextValue(style.width)
      : 0;

  const dropZoneHeight =
    style.height && typeof style.height === "string"
      ? parseCssTextValue(style.height)
      : 0;

  const dropZoneSize = isVerticalTimeline ? dropZoneHeight : dropZoneWidth;

  const previewItemDimensions = useMemo((): ItemDimensions | null => {
    const duration = itemDrag?.duration;

    if (!duration) return null;

    const startTime =
      isDropzonePreviewMedia && timelineLoopPreviewMedia
        ? timelineLoopPreviewMedia.startTime
        : Math.max(
            item.start_time.valueOf(),
            timelineRef?.current?.state.visibleTimeStart ?? 0,
          );

    const endTime =
      isDropzonePreviewMedia && timelineLoopPreviewMedia
        ? timelineLoopPreviewMedia.endTime
        : startTime + duration;

    const itemDimensions = timelineRef?.current?.computeItemDimensions({
      itemTimeStart: startTime,
      itemTimeEnd: endTime,
    });

    const itemSize = isVerticalTimeline
      ? itemDimensions?.height
      : itemDimensions?.width;

    if (itemDimensions && itemSize && itemSize > dropZoneSize) {
      if (isVerticalTimeline) {
        return {
          ...itemDimensions,
          height: dropZoneSize,
        };
      }
      return {
        ...itemDimensions,
        width: dropZoneSize,
      };
    }

    return itemDimensions ?? null;
  }, [
    itemDrag?.duration,
    isDropzonePreviewMedia,
    timelineLoopPreviewMedia,
    item.start_time,
    timelineRef,
    isVerticalTimeline,
    dropZoneSize,
  ]);

  const previewItemSize = isVerticalTimeline
    ? previewItemDimensions?.height
    : previewItemDimensions?.width;

  const computeItemStartTime = useCallback(
    (
      e: DragEvent<HTMLDivElement>,
      itemStartPosition: number | null,
    ): {
      startTime: number | null;
      truncated: Truncated;
    } => {
      const itemStartTime = timelineRef?.current?.timeFromItemEvent(e);
      const visibleTimelineTimeEnd = timelineRef?.current?.state.visibleTimeEnd;
      const visibleTimelineTimeStart =
        timelineRef?.current?.state.visibleTimeStart;

      if (
        !itemStartTime ||
        !visibleTimelineTimeEnd ||
        !itemDrag?.duration ||
        !visibleTimelineTimeStart
      ) {
        return {
          startTime: null,
          truncated: null,
        };
      }

      const itemEndTime = itemStartTime + itemDrag.duration;
      const dropzoneStartTime = item.start_time.valueOf();
      const dropzoneEndTime = item.end_time.valueOf();
      const isVisibleStartDropzone =
        dropzoneStartTime >= visibleTimelineTimeStart;

      const isVisibleEndDropzone = dropzoneEndTime <= visibleTimelineTimeEnd;

      if (renderType === TimelineRenderTypes.SCHEDULING && !item.isOnBaseline) {
        return {
          startTime: dropzoneStartTime,
          truncated: null,
        };
      }

      const visibleDropzoneEndTime = Math.min(
        dropzoneEndTime,
        visibleTimelineTimeEnd,
      );

      const visibleDropzoneStartTime = Math.max(
        dropzoneStartTime,
        visibleTimelineTimeStart,
      );

      const visibleDropzoneDuration =
        visibleDropzoneEndTime - visibleDropzoneStartTime;

      if (itemStartPosition === 0) {
        if (visibleDropzoneDuration < itemDrag.duration) {
          if (!isVisibleStartDropzone && !isVisibleEndDropzone) {
            if (itemStartTime < dropzoneStartTime) {
              return {
                startTime: dropzoneStartTime,
                truncated: "start-end",
              };
            }

            if (itemEndTime > dropzoneEndTime) {
              return {
                startTime: dropzoneEndTime - itemDrag.duration,
                truncated: "start-end",
              };
            }

            if (isLoopTimeline) {
              if (isDropzoneOnStartMaxRange) {
                return {
                  startTime: dropzoneEndTime - itemDrag.duration,
                  truncated: "start-end",
                };
              }

              if (isDropzoneOnEndMaxRange) {
                return {
                  startTime: dropzoneStartTime,
                  truncated: "start-end",
                };
              }
            }

            return {
              startTime: itemStartTime,
              truncated: "start-end",
            };
          }

          if (!isVisibleEndDropzone) {
            return {
              startTime: dropzoneStartTime,
              truncated: "end",
            };
          }

          if (
            isLoopTimeline &&
            hasDropzonesOnMaxRange &&
            isVisibleEndDropzone &&
            isVisibleStartDropzone
          ) {
            if (isDropzoneOnStartMaxRange) {
              const computedPreviewMediaStartTime =
                computeItemStartTimeLoopTimeline({
                  endTime: dropzoneEndTime,
                  duration: itemDrag.duration,
                  minTimeStart: minTimeStartTimeline,
                  maxTimeEnd: maxTimeEndTimeline,
                });

              dispatch({
                type: "SET_LOOP_TIMELINE_PREVIEW_MEDIA",
                media: {
                  startTime: computedPreviewMediaStartTime,
                  endTime: dropzoneOnEndMaxRange.end_time.valueOf(),
                  dropzoneId: dropzoneOnEndMaxRange.id,
                },
              });

              return {
                startTime: dropzoneEndTime - itemDrag.duration,
                truncated: "start",
              };
            }

            if (isDropzoneOnEndMaxRange) {
              const computedPreviewMediaEndTime =
                computeItemEndTimeLoopTimeline({
                  startTime: dropzoneStartTime,
                  duration: itemDrag.duration,
                  minTimeStart: minTimeStartTimeline,
                  maxTimeEnd: maxTimeEndTimeline,
                });

              dispatch({
                type: "SET_LOOP_TIMELINE_PREVIEW_MEDIA",
                media: {
                  startTime: dropzoneOnStartMaxRange.start_time.valueOf(),
                  endTime: computedPreviewMediaEndTime,
                  dropzoneId: dropzoneOnStartMaxRange.id,
                },
              });

              return {
                startTime: dropzoneStartTime,
                truncated: "end",
              };
            }
          }

          return {
            startTime: dropzoneEndTime - itemDrag.duration,
            truncated: "start",
          };
        }

        if (!isVisibleStartDropzone) {
          return {
            startTime: visibleDropzoneStartTime,
            truncated: null,
          };
        }

        return {
          startTime: dropzoneStartTime,
          truncated: null,
        };
      }

      if (itemStartTime < dropzoneStartTime) {
        return {
          startTime: dropzoneStartTime,
          truncated: "end",
        };
      }

      if (itemEndTime > dropzoneEndTime) {
        if (visibleDropzoneDuration < itemDrag.duration) {
          return {
            startTime: dropzoneEndTime - itemDrag.duration,
            truncated: "start",
          };
        }
        return {
          startTime: dropzoneEndTime - itemDrag.duration,
          truncated: null,
        };
      }

      if (itemEndTime > visibleTimelineTimeEnd) {
        if (
          isVisibleEndDropzone ||
          visibleDropzoneDuration >= itemDrag.duration
        ) {
          return {
            startTime: visibleTimelineTimeEnd - itemDrag.duration,
            truncated: null,
          };
        }

        if (isVisibleStartDropzone) {
          return {
            startTime: dropzoneStartTime,
            truncated: "end",
          };
        }
      }

      return {
        startTime: itemStartTime,
        truncated: null,
      };
    },
    [
      timelineRef,
      itemDrag?.duration,
      item,
      renderType,
      isLoopTimeline,
      hasDropzonesOnMaxRange,
      isDropzoneOnStartMaxRange,
      isDropzoneOnEndMaxRange,
      minTimeStartTimeline,
      maxTimeEndTimeline,
      dispatch,
      dropzoneOnEndMaxRange,
      dropzoneOnStartMaxRange,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttleSetPreviewItemStartTime = useCallback(
    throttle(
      (e: DragEvent<HTMLDivElement>, itemStartPosition: number | null) => {
        const { startTime, truncated } = computeItemStartTime(
          e,
          itemStartPosition,
        );
        setPreviewItemStartTime(startTime);
        setTruncatedPreviewItem(truncated);
      },
      100,
    ),
    [setPreviewItemStartTime, computeItemStartTime],
  );

  const computeItemStartPosition = useCallback(
    (startPosition: number): number | null => {
      if (!previewItemSize) {
        return null;
      }

      if (
        startPosition < MAGNETIC_ZONE_SIZE ||
        (renderType === TimelineRenderTypes.SCHEDULING && !item.isOnBaseline) ||
        isLoopTimeline
      ) {
        return 0;
      }

      if (startPosition + previewItemSize > dropZoneSize) {
        return dropZoneSize - previewItemSize;
      }

      return startPosition;
    },
    [previewItemSize, renderType, item, isLoopTimeline, dropZoneSize],
  );

  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      e.persist();
      const { currentTarget } = e;
      const targetDomReact = currentTarget.getBoundingClientRect();
      const targetLeft = targetDomReact.left;
      const targetTop = targetDomReact.top;
      const pointerY = e.clientY;
      const pointerX = e.clientX;
      const currentLeftPosition = pointerX - targetLeft;
      const currentTopPosition = pointerY - targetTop;

      const currentStartPosition = isVerticalTimeline
        ? currentTopPosition
        : currentLeftPosition;

      const updatedItemStartPosition =
        computeItemStartPosition(currentStartPosition);

      if (previewItemStartPosition !== updatedItemStartPosition) {
        setPreviewItemStartPosition(updatedItemStartPosition);
        throttleSetPreviewItemStartTime(e, updatedItemStartPosition);
      }
    },
    [
      computeItemStartPosition,
      isVerticalTimeline,
      previewItemStartPosition,
      throttleSetPreviewItemStartTime,
    ],
  );

  const handleDragLeave = useCallback((): void => {
    setPreviewItemStartPosition(null);
    setPreviewItemStartTime(null);

    if (isLoopTimeline) {
      dispatch({
        type: "DELETE_LOOP_TIMELINE_PREVIEW_MEDIA",
      });
    }

    if (activeDropZoneId === item.id) {
      dispatch({
        type: "SET_ACTIVE_DROP_ZONE",
        dropZoneId: null,
      });
    }
  }, [activeDropZoneId, dispatch, isLoopTimeline, item.id]);

  const handleDragEnter = useCallback((): void => {
    if ((!activeDropZoneId || activeDropZoneId !== item.id) && canDrop) {
      dispatch({
        type: "SET_ACTIVE_DROP_ZONE",
        dropZoneId: item.id,
      });
    }
  }, [item.id, activeDropZoneId, dispatch, canDrop]);

  const showDropZone = isDragging && !isReplaceMode;

  const computeStartPreviewItemPositionLoopTimeline = useCallback(():
    | number
    | null => {
    if (isDropzonePreviewMedia) {
      if (isDropzoneOnEndMaxRange && previewItemSize) {
        return dropZoneSize - previewItemSize;
      }
      if (isDropzoneOnStartMaxRange) {
        return 0;
      }
    }
    return previewItemStartPosition;
  }, [
    dropZoneSize,
    isDropzoneOnEndMaxRange,
    isDropzoneOnStartMaxRange,
    isDropzonePreviewMedia,
    previewItemSize,
    previewItemStartPosition,
  ]);

  const computeTruncatedPreviewMediaLoopTimeline = (): Truncated => {
    if (isDropzoneOnStartMaxRange) {
      return "start";
    }

    if (isDropzoneOnEndMaxRange) {
      return "end";
    }

    return truncatedPreviewItem;
  };

  const getDropzoneItemRendererByType = (): JSX.Element | null => {
    const startTimeMedia = isDropzonePreviewMedia
      ? timelineLoopPreviewMedia.startTime
      : previewItemStartTime;

    const startPosition = isLoopTimeline
      ? computeStartPreviewItemPositionLoopTimeline()
      : previewItemStartPosition;

    const truncated = isDropzonePreviewMedia
      ? computeTruncatedPreviewMediaLoopTimeline()
      : truncatedPreviewItem;

    if (
      startPosition === null ||
      !previewItemSize ||
      startTimeMedia === null ||
      itemDrag === null
    ) {
      return null;
    }

    switch (renderType) {
      case TimelineRenderTypes.TEMPLATING: {
        return (
          <TemplateDropZoneItemRenderer
            dropZoneSize={dropZoneSize}
            isVertical={isVerticalTimeline}
            itemDrag={itemDrag}
            size={previewItemSize}
            startPosition={startPosition}
            truncated={truncated}
            truncatedDropzone={truncatedDropzone}
          />
        );
      }
      case TimelineRenderTypes.SCHEDULING:
      default: {
        const { previewMediaTooltipRef } = props;

        return (
          <SchedulingDropZoneItemRenderer
            isVertical={isVerticalTimeline}
            item={item}
            itemDrag={itemDrag}
            previewMediaTooltipRef={previewMediaTooltipRef}
            size={previewItemSize}
            startPosition={startPosition}
            startTimeMedia={startTimeMedia}
            truncated={truncated}
            truncatedDropzone={truncatedDropzone}
          />
        );
      }
    }
  };

  const dropzoneClassNames = useMemo((): string => {
    const dropzoneClassNamesBase = classNames(
      "flex size-full rounded-[0.46875rem] items-center justify-center flex relative",
    );

    if (!canDrop) {
      return classNames("bg-destructive/20", dropzoneClassNamesBase);
    }

    if (isOver) {
      return classNames("bg-primary/10", dropzoneClassNamesBase);
    }

    return classNames("bg-primary/10", dropzoneClassNamesBase);
  }, [canDrop, isOver]);

  const dropzoneBorderRadius = useMemo(
    () =>
      getBorderRadiusTimelineItem({
        truncated: truncatedDropzone,
        isVerticalTimeline,
      }),
    [truncatedDropzone, isVerticalTimeline],
  );

  const borderColor = !canDrop ? COLORS.red[400] : COLORS.primary.DEFAULT;

  const dropzoneBorder = useMemo(
    () =>
      getBorderWidthPosition(
        `${DROPZONE_BORDER_SIZE}px dashed ${borderColor}`,
        truncatedDropzone,
        isVerticalTimeline,
      ),
    [borderColor, isVerticalTimeline, truncatedDropzone],
  );

  const showPreviewMediaItem =
    previewItemSize &&
    ((item.id === activeDropZoneId &&
      itemDrag &&
      previewItemStartTime &&
      previewItemStartPosition !== null) ??
      isDropzonePreviewMedia);

  if (!showDropZone) {
    return null;
  }

  return (
    <div
      ref={ref}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onTouchStart={onTouchStart}
      style={{
        ...style,
        zIndex: MAX_TIMELINE_Z_INDEX,
        overflow: "hidden",
      }}
    >
      <div
        ref={dropRef}
        className={dropzoneClassNames}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        style={{
          ...dropzoneBorder,
          borderRadius: dropzoneBorderRadius,
        }}
      >
        {canDrop ? (
          <React.Fragment>
            {!showPreviewMediaItem && (
              <span className="text-foreground pointer-events-none truncate font-semibold">
                {t("addHere")}
              </span>
            )}
            {showPreviewMediaItem ? getDropzoneItemRendererByType() : null}
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
}

export default DropZoneItemRenderer;
